import { useEffect, useState } from 'react';

import { Box, Page, Spinner, TextButton } from '@zillow/constellation-10';

import { PathsManifest } from '@zg-rentals/paths-manifest';

import { AdminNav, HeaderNavigation, MobileHeaderNav, MockUserNav, VerticalNavigation } from '..';
import TopNavBar from '../TopNavBar';

import { getCookieAsBoolean, ZRM_IA_NAV_ENABLED } from '../../utils/featureFlags';

import {
  appLayoutContainerRecipe,
  bodyContainerRecipe,
  footerContainerStyles,
  layoutContainerRecipe,
  verticalNavigationContainerRecipe,
} from './styles';

import type React from 'react';
import type { User } from '../../types';
import type { Props as HeaderNavigationProps } from '../HeaderNavigation';

const SIDE_NAV_DENY_LIST = [
  // will match any string that starts with /rental-manager and optionally ends with a trailing slash
  /^\/rental-manager(?:\/$)?$/,
  // will match any string that starts with "/rental-manager/leasing/colandlord" and optionally ends with a trailing slash, and then any characters
  /^\/rental-manager\/leasing\/colandlord(?:\/$)?.*$/,
  // will match any string that starts with "/rental-manager/property-condition/colandlord" and optionally ends with a trailing slash, and then any characters
  /^\/rental-manager\/property-condition\/colandlord(?:\/$)?.*$/,
  // will match any string that starts with "/renter-hub/property-condition" and optionally ends with a trailing slash, and then any characters
  /^\/renter-hub\/property-condition(?:\/$)?.*$/,
];

export type Props = HeaderNavigationProps & {
  // set the loading state of the container, will render a full-page spinner
  isLoading?: boolean;
  // hides all navigation components (header, side nav, and mobile nav)
  hideNavigation?: boolean;
  // when true, renders sidebar navigation; when false, renders top header navigation
  useLeftNav?: boolean;
  // the current relative path so the nav link can be highlighted
  pathname: string;
  // page content
  children: React.ReactNode;
  userModel: User;
  isInMobileWebView?: boolean;
  // hides the privacy policy footer (ex. in mobile webview or wizard views)
  hideFooter?: boolean;
  // ZRM-16458: SSR-resolved opt-in for the new IA nav. When provided (e.g. the
  // host reads the request cookie or experiment assignment on the server), it's
  // used directly so the server and first client render agree (no hydration
  // flash). When omitted, the module falls back to a post-mount cookie read.
  iaNavEnabled?: boolean;
};

const getShouldUseLeftNav = (shouldBlockViaDenyList: boolean, useLeftNav: boolean) => {
  return !shouldBlockViaDenyList && useLeftNav;
};

const renderLoading = () => (
  <Page.Root data-testid="LoadingContainer" css={{ backgroundColor: 'bg.neutral', height: '100%' }}>
    <Page.Content css={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner size="lg" />
    </Page.Content>
  </Page.Root>
);

const AppLayoutContainer = (props: Props) => {
  const {
    hideNavigation = false,
    useLeftNav = true,
    isLoading = false,
    pathname,
    children = null,
    userModel = {} as User,
    isInMobileWebView = false,
    hideFooter = false,
    brand,
    useRelativePaths,
    host,
    iaNavEnabled: iaNavEnabledProp,
  } = props;

  const blockPath = SIDE_NAV_DENY_LIST.some((pattern) => {
    if (typeof pattern === 'string') {
      // For exact paths, check for an exact match
      return pathname === pattern;
    } else {
      // For patterns, test the path against the regex
      return pattern.test(pathname);
    }
  });

  const shouldUseLeftNav = getShouldUseLeftNav(blockPath, useLeftNav);

  // ZRM-16458: opt-in to the new IA navigation (top bar + slimmed left nav).
  // Prefer the SSR-resolved prop (no hydration flash); otherwise fall back to a
  // post-mount cookie read so QA can opt in via the /featureFlags cookie without
  // any host wiring. The cookie is read after mount (not during render) because
  // it is unavailable during SSR and an inline read would mismatch on hydration.
  const [cookieIaNavEnabled, setCookieIaNavEnabled] = useState(false);

  useEffect(() => {
    if (iaNavEnabledProp === undefined) {
      setCookieIaNavEnabled(getCookieAsBoolean(ZRM_IA_NAV_ENABLED));
    }
  }, [iaNavEnabledProp]);

  // TODO(ZRM-16458): for real rollout, source the opt-in from the Test B
  // experiment assignment delivered in the SSR user/features payload (like the
  // other `display*` flags) instead of a cookie. That value is available at SSR
  // (flash-free) and works for real users; the cookie then becomes a QA-only
  // override passed via `iaNavEnabledProp`.
  const iaNavEnabled = iaNavEnabledProp ?? cookieIaNavEnabled;

  const useAbsolutePaths = !useRelativePaths;
  const paths = new PathsManifest('/rental-manager', brand, useAbsolutePaths, host).getAllPaths();
  const { signInPage: signInPageFunc } = paths;

  const signInPage = signInPageFunc(pathname) || '';

  const showIaTopBar = shouldUseLeftNav && iaNavEnabled && hideNavigation === false;

  const renderLayout = () => {
    return (
      <>
        {showIaTopBar && (
          <Box data-testid="TopNavBarContainer" css={{ display: { base: 'none', xxl: 'block' } }}>
            <TopNavBar {...props} />
          </Box>
        )}

        <Box
          data-testid={shouldUseLeftNav ? 'LeftNavLayoutContainer' : 'HeaderNavLayoutContainer'}
          css={layoutContainerRecipe.raw({ isLeftNav: shouldUseLeftNav })}
        >
          {!isInMobileWebView && !hideNavigation ? (
            <Box css={{ display: { base: 'block', xxl: 'none' } }}>
              <MobileHeaderNav {...props} />
            </Box>
          ) : null}

          <Box css={{ display: { base: 'none', xxl: 'block' } }}>
            {shouldUseLeftNav ? (
              <>
                {hideNavigation === false && (
                  <Box
                    data-testid="LeftNavContainer"
                    css={verticalNavigationContainerRecipe.raw({ hideNavigation, iaTopBar: showIaTopBar })}
                  >
                    <VerticalNavigation displayMainMaintenanceTracker={false} {...props} topBar={iaNavEnabled} />
                  </Box>
                )}
              </>
            ) : (
              <>{hideNavigation === false && <HeaderNavigation {...props} />}</>
            )}
          </Box>

          <Box
            data-testid="BodyContainer"
            css={bodyContainerRecipe.raw({ hideNavigation, isLeftNav: shouldUseLeftNav })}
          >
            <Box data-testid="BodyContentContainer" css={{ width: '100%', minHeight: '100%' }}>
              {children}
            </Box>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Box
      data-testid="AppLayoutContainer"
      data-ia-nav={iaNavEnabled}
      css={appLayoutContainerRecipe.raw({ isLeftNav: shouldUseLeftNav })}
    >
      <Box css={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200000 }}>
        <MockUserNav brand={brand} userModel={userModel} />
        <AdminNav brand={brand} paths={paths} signInPage={signInPage} userModel={userModel} pathname={pathname} />
      </Box>

      {blockPath ? renderLayout() : isLoading ? renderLoading() : renderLayout()}

      {!hideFooter && (
        <Page.Root css={footerContainerStyles.raw({ useLeftNav: shouldUseLeftNav })}>
          <Page.Content css={{ textAlign: 'end' }}>
            <TextButton textStyle="body-sm" title="Privacy Policy" tone="neutral" asChild>
              <a href="https://www.zillow.com/corporate/zg-privacy-notice/">Privacy Policy</a>
            </TextButton>
          </Page.Content>
        </Page.Root>
      )}
    </Box>
  );
};

export default AppLayoutContainer;
